import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLogs } from "../context/LogsContext";

export const useAnswerLogData = (answerId) => {
  const { t } = useTranslation();
  const {
    fetchAnswerLogs,
    answerLogsMap,
    isLoadingAnswerLogs,
    error: contextError,
  } = useLogs();
  const [error, setError] = useState(null);
  const [conversationData, setConversationData] = useState([]);
  const [info, setInfo] = useState({
    summary: "",
    question: "",
    answer: "",
    sources: [],
    error: null,
    success_status: "",
    product: "",
    macros_suggestions: null,
    selected_macro_id: null,
    selected_macro_source: null,
    selected_macro: null,
  });

  // Fetch answer logs
  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      if (!answerId) return;

      // If we already have logs for this answer, don't fetch again
      if (answerLogsMap[answerId]) return;

      try {
        if (isMounted) {
          await fetchAnswerLogs(answerId);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(t("errors.failedToFetchAnswerLogs"));
        }
      }
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [answerId, fetchAnswerLogs, t, answerLogsMap]);

  // Process logs data when logs change in context
  useEffect(() => {
    if (answerLogsMap[answerId]) {
      try {
        // Extract information directly from the response object
        const responseData = answerLogsMap[answerId];

        if (responseData) {
          let {
            logs = [],
            question = "",
            answer = "",
            summary = "",
            sources = [],
            error = null,
            success_status = "",
            product = "",
            macros_suggestions = null,
            selected_macro_id = null,
            selected_macro_source = null,
            selected_macro = null,
          } = responseData;

          // If data not in answers table, extract from logs
          if (logs && logs.length > 0) {
            // Extract summary and question from getSummaryResponse log
            if (!summary || !question) {
              const summaryLog = logs.find(log => log.step === "getSummaryResponse");
              if (summaryLog) {
                try {
                  const summaryResponse = JSON.parse(summaryLog.response);
                  if (!summary && summaryResponse.summary) {
                    summary = summaryResponse.summary;
                  }
                  if (!question && summaryResponse.currentQuestion) {
                    question = summaryResponse.currentQuestion;
                  }
                  if (!product && summaryResponse.product) {
                    product = summaryResponse.product;
                  }
                } catch (e) {
                  console.error("Error parsing summary log:", e);
                }
              }
            }

            // Extract product and macros from findMacrosSuggestions log
            if (!product || !macros_suggestions) {
              const macrosLog = logs.find(log => log.step === "findMacrosSuggestions");
              if (macrosLog) {
                try {
                  const promptData = JSON.parse(macrosLog.prompt);
                  const responseLogData = JSON.parse(macrosLog.response);
                  if (!product) {
                    product = promptData?.product || responseLogData?.product || "";
                  }
                  if (!macros_suggestions) {
                    macros_suggestions = responseLogData?.suggestions || null;
                  }
                } catch (e) {
                  console.error("Error parsing macros log:", e);
                }
              }
            }

            // Extract answer and sources from generateAnswerFromSections log
            if (!answer || !sources) {
              const answerLog = logs.find(log => log.step === "generateAnswerFromSections");
              if (answerLog) {
                try {
                  const answerResponse = JSON.parse(answerLog.response);
                  if (!answer && answerResponse.answer) {
                    answer = answerResponse.answer;
                  }
                  if (!sources && answerResponse.sources) {
                    sources = answerResponse.sources;
                  }
                } catch (e) {
                  console.error("Error parsing answer log:", e);
                }
              }
            }
          }

          // Process conversation data if available in the first log
          if (logs.length > 0 && logs[0] && logs[0].prompt) {
            processMessages(logs[0]);
          }

          setInfo({
            summary,
            question,
            answer,
            sources,
            error,
            success_status,
            product,
            macros_suggestions,
            selected_macro_id,
            selected_macro_source,
            selected_macro,
          });
        }
      } catch (err) {
        console.error("Error processing logs:", err);
        setError(t("errors.failedToProcessLogs"));
      }
    }
  }, [answerId, answerLogsMap, t]);

  const processMessages = (firstLog) => {
    try {
      const promptData = JSON.parse(firstLog.prompt);
      if (promptData && promptData.messages && promptData.messages.length > 0) {
        // Find the user message that contains the conversation array
        const userMessage = promptData.messages.find(msg => msg.role === "user");
        if (userMessage && userMessage.content) {
          const messages = JSON.parse(userMessage.content);
          setConversationData(messages || []);
        } else {
          setConversationData([]);
        }
      } else {
        setConversationData([]);
      }
    } catch (err) {
      console.error("Error processing messages:", err);
      setConversationData([]);
    }
  };

  // Get logs organized by step name
  const getLogsByStep = () => {
    if (!answerLogsMap[answerId] || !answerLogsMap[answerId].logs) {
      return [];
    }

    // Map of step names to their display order
    const stepOrder = {
      getSummaryResponse: 0,
      findMacrosSuggestions: 1,
      validateQuestionSimilarity: 2,
      filterSections: 3,
      generateAnswerFromSections: 4,
    };

    // Sort logs by their step order
    return [...answerLogsMap[answerId].logs].sort((a, b) => {
      const aOrder = stepOrder[a.step] !== undefined ? stepOrder[a.step] : 999;
      const bOrder = stepOrder[b.step] !== undefined ? stepOrder[b.step] : 999;
      return aOrder - bOrder;
    });
  };

  return {
    isLoadingAnswerLogs,
    error: error || contextError,
    conversationData,
    info,
    logsData: getLogsByStep(),
  };
};
